"use client";

import React, { createContext, useContext, useEffect, useState, Suspense } from "react";
import { useSearchParams } from "next/navigation";
import {
  REFERRAL_COOKIE_CODE,
  REFERRAL_COOKIE_NAME,
  getCookie,
} from "@/lib/utils/referral";
import { trackReferralClick } from "@/lib/affiliate/trackClick";

interface ReferralContextType {
  referrerCode: string | null;
  referrerName: string | null;
  dismissBanner: () => void;
  isBannerDismissed: boolean;
}

const ReferralContext = createContext<ReferralContextType>({
  referrerCode: null,
  referrerName: null,
  dismissBanner: () => {},
  isBannerDismissed: false,
});

export const useReferral = () => useContext(ReferralContext);

function ReferralTrackerInner() {
  const searchParams = useSearchParams();
  const refCode = searchParams.get("ref");
  
  useEffect(() => {
    if (!refCode) return;

    // The server records the click, sets the visitor cookies, and the helper fires the state sync event.
    trackReferralClick(refCode);
  }, [refCode]);
  
  return null;
}

export function ReferralProvider({ children }: { children: React.ReactNode }) {
  const [referrerCode, setReferrerCode] = useState<string | null>(() => getCookie(REFERRAL_COOKIE_CODE));
  const [referrerName, setReferrerName] = useState<string | null>(() => getCookie(REFERRAL_COOKIE_NAME));
  const [isBannerDismissed, setIsBannerDismissed] = useState(() => {
    if (typeof window !== "undefined") {
      return sessionStorage.getItem("hirecarta_referral_banner_dismissed") === "true";
    }
    return false;
  });
  
  const updateReferralState = () => {
    const code = getCookie(REFERRAL_COOKIE_CODE);
    const name = getCookie(REFERRAL_COOKIE_NAME);
    setReferrerCode(code);
    setReferrerName(name);
  };
  
  useEffect(() => {
    // Listen for updates when a new ref code is parsed in the URL
    window.addEventListener("hirecarta_referral_update", updateReferralState);
    
    return () => {
      window.removeEventListener("hirecarta_referral_update", updateReferralState);
    };
  }, []);
  
  const dismissBanner = () => {
    sessionStorage.setItem("hirecarta_referral_banner_dismissed", "true");
    setIsBannerDismissed(true);
  };
  
  return (
    <ReferralContext.Provider
      value={{
        referrerCode,
        referrerName,
        dismissBanner,
        isBannerDismissed,
      }}
    >
      <Suspense fallback={null}>
        <ReferralTrackerInner />
      </Suspense>
      {children}
    </ReferralContext.Provider>
  );
}
